import { numberWithCommas } from 'shared/utils/numberUtils';
import { parsingRows } from './ContributionMappingTable/worker';

const testDonorAccount = 'fundertest@mailinator.com';
export const investmentUploadColumns = {
  organization: ['Organization', 'Organization Name'],
  fundedProgramName: ['Funded Program Name', 'Funded Program', 'Program'],
  cashAmount: ['Cash Amount'],
  goodsAmount: ['Goods Amount'],
  volunteerismAmount: ['Volunteerism Amount'],
  servicesAmount: ['Services Amount'],
  contactFirstName: ['Contact First Name', 'First Name'],
  contactLastName: ['Contact Last Name', 'Last Name'],
  contactEmail: ['Contact Email (NPO)', 'Contact Email', 'Email'],
  externalRecordId: ['External Record ID'],
  relationshipManagerEmail: [
    'Relationship Manager Email (Donor)',
    'Relationship Manager Email',
    'Manager Email',
  ],
  allocateDueDate: ['Allocate Due Date'],
  submitDueDate: ['Submit Due Date'],
  resolveDueDate: ['Resolve Due Date'],
  publishDueDate: ['Publish Due Date'],
  interimDueDate: ['Interim Due Date'],
  finalizeDueDate: ['Finalize Due Date'],
  portfolioName: ['Portfolio Name', 'Portfolio'],
  year: ['Investment Year', 'Year'],
  tag1: ['Tag', 'Tag 1', 'Tag1'],
  tag2: ['Tag 2', 'Tag2'],
  tag3: ['Tag 3', 'Tag3'],
  tag4: ['Tag 4', 'Tag4'],
  tag5: ['Tag 5', 'Tag5'],
  tag6: ['Tag 6', 'Tag6'],
  tag7: ['Tag 7', 'Tag7'],
  tag8: ['Tag 8', 'Tag8'],
  tag9: ['Tag 9', 'Tag9'],
  tag10: ['Tag 10', 'Tag10'],
} as const;

export type InvestmentColumnKeys = keyof typeof investmentUploadColumns;

export const investmentOptionalColumns: InvestmentColumnKeys[] = [
  'relationshipManagerEmail',
  'goodsAmount',
  'volunteerismAmount',
  'servicesAmount',
  'allocateDueDate',
  'submitDueDate',
  'resolveDueDate',
  'publishDueDate',
  'interimDueDate',
  'finalizeDueDate',
  'externalRecordId',
  'tag1',
  'tag2',
  'tag3',
  'tag4',
  'tag5',
  'tag6',
  'tag7',
  'tag8',
  'tag9',
  'tag10',
];

export const isInvestmentColumnDollar = (key: InvestmentColumnKeys) =>
  key === 'cashAmount' ||
  key === 'goodsAmount' ||
  key === 'servicesAmount' ||
  key === 'volunteerismAmount';

export const isInvestmentColumnDate = (key: InvestmentColumnKeys) =>
  key === 'allocateDueDate' ||
  key === 'submitDueDate' ||
  key === 'resolveDueDate' ||
  key === 'publishDueDate' ||
  key === 'interimDueDate' ||
  key === 'finalizeDueDate';

const replaceSlashWithDash = (v: string): string => {
  return v.replace(/\//g, '-');
};

// Field length limits mirror the backend model column definitions in
export const PROGRAM_NAME_MAX_LENGTH = 255;
export const EXTERNAL_ID_MAX_LENGTH = 100;

const defaultNameColumnValidation = (v: string) => {
  if (v.length < 1 || v.length > 250) return false;
  for (let i = 0; i < v.length; i++) {
    if (v.charCodeAt(i) > 0xffff) {
      return false;
    }
  }
  return true;
};
const defaultStringColumnValidation = (v: string) => {
  if (v.length < 2 || v.length > 250) return false;
  for (let i = 0; i < v.length; i++) {
    if (v.charCodeAt(i) > 0xffff) {
      return false;
    }
  }
  return true;
};
const defaultProgramNameColumnValidation = (v: string) => {
  if (v.length < 2 || v.length > PROGRAM_NAME_MAX_LENGTH) return false;
  for (let i = 0; i < v.length; i++) {
    if (v.charCodeAt(i) > 0xffff) {
      return false;
    }
  }
  return true;
};
const regexYYYYMMDD = /^\d{4}-\d{2}-\d{2}$/;
const regexMMDDYYYY = /^\d{2}-\d{2}-\d{4}$/;

/* const defaultDateColumnValidation = (v: string): boolean => {
  v = replaceSlashWithDash(v);
  if (regexYYYYMMDD.test(v) || regexMMDDYYYY.test(v)) {
    return !isNaN(new Date(v).getTime());
  }
  return false;
}; */

const defaultOptionalDateColumnValidation = (v: string): boolean => {
  v = replaceSlashWithDash(v);
  if (!v) return true;
  if (regexYYYYMMDD.test(v) || regexMMDDYYYY.test(v)) {
    return !isNaN(new Date(v).getTime());
  }
  return false;
};
const dollarAmountRegex = /^\$?(\d{1,3}(,\d{3})*|\d+)(\.\d+)?$/;
const defaultAmountColumnValidation = (v: string) => dollarAmountRegex.test(v);
const defaultOptionalAmountColumnValidation = (v: string) =>
  v ? dollarAmountRegex.test(v) : true;
const emailRegex = /^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+\.[a-zA-Z0-9-.]+$/;
const defaultEmailColumnValidation = (v: string) => emailRegex.test(v);
const defaultOptionalEmailColumnValidation = (v: string) =>
  v ? emailRegex.test(v) : true;
const defaultOptionalColumnValidation = (v: string) => v.length < 250;
const externalRecordIdColumnValidation = (v: string) =>
  v.length <= EXTERNAL_ID_MAX_LENGTH;
const defaultYearColumnValidation = (v: string) => {
  const num = Number(v);
  if (!num) return false;
  return num > 2000 && num < 2200;
};
export const investmentUploadColumnValidations: {
  [id in InvestmentColumnKeys]: (v: string, row: string[]) => boolean;
} = {
  organization: defaultStringColumnValidation,
  fundedProgramName: defaultProgramNameColumnValidation,
  portfolioName: defaultStringColumnValidation,
  cashAmount: defaultAmountColumnValidation,
  goodsAmount: defaultOptionalAmountColumnValidation,
  volunteerismAmount: defaultOptionalAmountColumnValidation,
  servicesAmount: defaultOptionalAmountColumnValidation,
  contactFirstName: defaultNameColumnValidation,
  contactLastName: defaultNameColumnValidation,
  contactEmail: defaultEmailColumnValidation,
  relationshipManagerEmail: defaultOptionalEmailColumnValidation,
  externalRecordId: externalRecordIdColumnValidation,
  allocateDueDate: defaultOptionalDateColumnValidation,
  submitDueDate: defaultOptionalDateColumnValidation,
  resolveDueDate: defaultOptionalDateColumnValidation,
  publishDueDate: defaultOptionalDateColumnValidation,
  interimDueDate: defaultOptionalDateColumnValidation,
  finalizeDueDate: defaultOptionalDateColumnValidation,
  year: defaultYearColumnValidation,
  tag1: defaultOptionalColumnValidation,
  tag2: defaultOptionalColumnValidation,
  tag3: defaultOptionalColumnValidation,
  tag4: defaultOptionalColumnValidation,
  tag5: defaultOptionalColumnValidation,
  tag6: defaultOptionalColumnValidation,
  tag7: defaultOptionalColumnValidation,
  tag8: defaultOptionalColumnValidation,
  tag9: defaultOptionalColumnValidation,
  tag10: defaultOptionalColumnValidation,
};

export const investmentUploadColumnKeys = Object.keys(investmentUploadColumns);
const investmentUploadColumnValues = Object.values(investmentUploadColumns).map(
  arr => arr[0],
);
const investmentUploadColumnNames =
  investmentUploadColumnValues.join(',') + '\n';

const investmentColumnsMapping = () => {
  const result: [string, InvestmentColumnKeys][] = [];
  for (const k of investmentUploadColumnKeys) {
    const key = k as InvestmentColumnKeys;
    const element = investmentUploadColumns[k] as string[];
    for (const e of element) {
      result.push([e, key]);
    }
  }
  return result;
};
export const investmentColumnsMappingArray = investmentColumnsMapping();

export const downloadSampleCSVFileUrl = URL.createObjectURL(
  new Blob([investmentUploadColumnNames], {
    type: 'text/csv;charset=utf-8;',
  }),
);

export const downloadDataUrl = (data: string[][]) => {
  let finalData = '';

  for (let i = 0; i < data.length; i++) {
    const row = data[i];
    for (let j = 1; j < row.length; j++) {
      const element = row[j];
      finalData += j < row.length - 1 ? element + ',' : element;
    }
    if (i < data.length - 1) {
      finalData += '\n';
    }
  }
  const url = URL.createObjectURL(
    new Blob([finalData], { type: 'text/csv;charset=utf-8;' }),
  );
  const link = document.createElement('a');
  link.href = url;
  link.download = 'Impact Receipt Investment Upload Template.csv';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const investmentPrettifyText = (
  systemKey: InvestmentColumnKeys | undefined,
  value: string,
) => {
  if (
    value &&
    systemKey &&
    isInvestmentColumnDollar(systemKey) &&
    !value.startsWith('$')
  ) {
    value = '$' + value;
    value = numberWithCommas(value);
  }
  return value;
};

const filterUndefined = (
  ...args: (string | undefined)[]
): { tag: string }[] => {
  return args.reduce((acc: { tag: string }[], arg) => {
    if (
      arg !== undefined &&
      arg !== '' &&
      arg !== '\r' &&
      arg !== ' ' &&
      arg !== '\\r'
    ) {
      acc.push({ tag: arg });
    }
    return acc;
  }, []);
};

const formatDate = (date: string | undefined): string | undefined => {
  if (!date) return date;
  date = replaceSlashWithDash(date);
  if (regexYYYYMMDD.test(date)) {
    return date;
  }
  if (regexMMDDYYYY.test(date)) {
    const [month, day, year] = date.split('-');
    return `${year}-${month}-${day}`;
  }
  return date;
};

const removeDollarSign = (val: string | undefined) => {
  return val && val[0] === '$' ? val.slice(1) : val;
};

export const investmentSaveMapping = (
  investments: ReturnType<typeof parsingRows>['result'],
) => {
  if (!investments) return [];
  const result: object[] = [];
  for (const i of Object.values(investments)) {
    if (!i) continue;
    result.push({
      ...i,
      portfolio: i.portfolioName,
      fundedProgramName: i.fundedProgramName,
      relationshipManagerEmail: i.relationshipManagerEmail || testDonorAccount,
      cashAmount: removeDollarSign(i.cashAmount),
      goodsAmount: removeDollarSign(i.goodsAmount),
      servicesAmount: removeDollarSign(i.servicesAmount),
      volunteerismAmount: removeDollarSign(i.volunteerismAmount),
      allocateDueDate: formatDate(i.allocateDueDate),
      submitDueDate: formatDate(i.submitDueDate),
      resolveDueDate: formatDate(i.resolveDueDate),
      publishDueDate: formatDate(i.publishDueDate),
      interimDueDate: formatDate(i.interimDueDate),
      finalizeDueDate: formatDate(i.finalizeDueDate),
      tag1: undefined,
      tag2: undefined,
      tag3: undefined,
      tag4: undefined,
      tag5: undefined,
      tag6: undefined,
      tag7: undefined,
      tag8: undefined,
      tag9: undefined,
      tag10: undefined,
      portfolioName: undefined,
      tags: filterUndefined(
        i.tag1,
        i.tag2,
        i.tag3,
        i.tag4,
        i.tag5,
        i.tag6,
        i.tag7,
        i.tag8,
        i.tag9,
        i.tag10,
      ),
    });
  }
  return result;
};
